import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { ticketsClient, type TicketCategory } from "../../Pages/Tickets/ticketsClient";
import { getUserDirectory, type DirectoryEntry } from "../Account/accountClient";
import { CollapsibleCard } from "../CollapsibleCard";

/**
 * Matrice « assigné par défaut par catégorie de ticket » : lignes = utilisateurs, colonnes =
 * catégories. Cocher une case fait de cet utilisateur un assigné par défaut de la catégorie : un
 * ticket sans assigné explicite compte alors dans son badge « Tickets (N) ». Chaque bascule
 * ré-enregistre le jeu COMPLET de la catégorie. Affichée sur la page Utilisateurs ; requiert
 * feature:tickets:write.
 *
 * Repliée par défaut (réglage secondaire) et soumise au FILTRE de la page : `visibleEmails` porte
 * les adresses des personnes affichées dans le tableau principal ; les lignes se limitent à
 * elles, de sorte que filtrer « Admins » ou chercher un nom réduit la matrice au même périmètre.
 * `visibleEmails` absent (ou null) = aucun filtre actif, on montre tout l'annuaire.
 */
const key = (category: string, email: string) => `${category}|${email.toLowerCase()}`;

export const TicketDefaultsCard: React.FC<{ visibleEmails?: ReadonlySet<string> | null }> = ({ visibleEmails }) => {
	const [categories, setCategories] = useState<TicketCategory[]>([]);
	const [directory, setDirectory] = useState<DirectoryEntry[]>([]);
	const [defaults, setDefaults] = useState<Set<string>>(new Set());
	const [saving, setSaving] = useState<string | null>(null);

	const load = async () => {
		const [cats, dir, defs] = await Promise.all([
			ticketsClient.categories().catch(() => []),
			getUserDirectory().catch(() => []),
			ticketsClient.defaultAssignees().catch(() => [])
		]);
		setCategories(cats);
		setDirectory(dir);
		setDefaults(new Set(defs.map((d) => key(d.category, d.email))));
	};

	useEffect(() => {
		load();
	}, []);

	const users = useMemo(
		() => directory.filter((d) => !!d.email).map((d) => ({ email: (d.email as string).toLowerCase(), name: d.name || d.email })),
		[directory]
	);

	// Les lignes visibles suivent le filtre de la page ; l'enregistrement, lui, porte TOUJOURS sur
	// le jeu complet de la catégorie (une case cochée pour quelqu'un de masqué n'est pas effacée).
	const visibles = useMemo(
		() => (visibleEmails ? users.filter((u) => visibleEmails.has(u.email)) : users),
		[users, visibleEmails]
	);

	const toggle = async (category: string, email: string) => {
		const next = new Set(defaults);
		const k = key(category, email);
		if (next.has(k)) next.delete(k);
		else next.add(k);
		setDefaults(next);
		// Ré-enregistre le jeu complet de la catégorie, filtre compris ou non.
		const emailsForCat = users.filter((u) => next.has(key(category, u.email))).map((u) => u.email);
		setSaving(category);
		try {
			await ticketsClient.setCategoryDefaults(category, emailsForCat);
		} catch {
			toast.error("Échec de l'enregistrement des assignés par défaut.");
			setDefaults(defaults); // revert
		} finally {
			setSaving(null);
		}
	};

	if (categories.length === 0 || users.length === 0) return null;

	return (
		<CollapsibleCard
			title="Assignés par défaut des tickets"
			subtitle="Un ticket sans assigné explicite est routé vers les utilisateurs cochés pour sa catégorie (leur badge « Tickets »)."
			count={visibles.length}
		>
			{visibles.length === 0 ? (
				<p className="text-sm text-slate-500">Aucun utilisateur ne correspond au filtre en cours.</p>
			) : (
				<div className="overflow-x-auto">
					<table className="min-w-full text-sm">
						<thead>
							<tr className="border-b border-slate-100 text-left text-xs text-slate-500">
								<th className="py-2 pr-4 font-semibold">Utilisateur</th>
								{categories.map((c) => (
									<th key={c.key} className="px-2 py-2 text-center font-semibold">{c.label}</th>
								))}
							</tr>
						</thead>
						<tbody>
							{visibles.map((u) => (
								<tr key={u.email} className="border-b border-slate-50">
									<td className="py-2 pr-4">
										<span className="font-medium text-slate-700">{u.name}</span>
										<span className="ml-2 text-xs text-slate-400">{u.email}</span>
									</td>
									{categories.map((c) => (
										<td key={c.key} className="px-2 py-2 text-center">
											<input
												type="checkbox"
												aria-label={`${u.email} : ${c.label}`}
												checked={defaults.has(key(c.key, u.email))}
												disabled={saving === c.key}
												onChange={() => toggle(c.key, u.email)}
											/>
										</td>
									))}
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
		</CollapsibleCard>
	);
};

export default TicketDefaultsCard;
